import { useContext } from 'react'
import { format, parseISO } from 'libs/dates'
import { useTranslation } from 'libs/i18n'

import { Currency, TableImage } from 'shared/components'
import { Detail } from 'shared/atoms'
import { useUserRoles } from 'shared/hooks'
import { StoreSettingsContext } from 'store'
import type { SaleItem } from 'features/sales/types/sales.types'

type SaleProps = {
	sale: SaleItem
	onSelect: (sale: SaleItem) => void
}

export const Sale = ({ sale, onSelect }: SaleProps) => {
	const { t } = useTranslation()
	const { isStaff } = useUserRoles()
	const storeCtx = useContext(StoreSettingsContext)
	const {
		account_name,
		product_name,
		product_sku,
		product_image,
		receipt_reference,
		created,
		quantity,
		booking,
		vat,
		vat_percentage,
		discount,
		commission,
		commission_percentage,
		net_profit,
		total,
		payout
	} = sale

	return (
		<li>
			<button
				type="button"
				aria-label={t('Sale details for {{ name }}', { name: product_name })}
				className="block w-full p-4 text-left transition-colors hover:bg-muted/40"
				onClick={() => onSelect(sale)}>
				<div className="flex items-start gap-3">
					<TableImage className="h-10 w-10" src={product_image} />
					<div className="min-w-0 flex-1">
						<p className="truncate text-[13px] font-semibold text-primary">{product_name}</p>
						<p className="truncate text-[11px] text-muted-foreground">{product_sku}</p>
					</div>
					<div className="shrink-0 text-right">
						<Currency
							className="block font-display text-[15px] font-bold tabular-nums"
							price={total}
						/>
						<span className="block text-[11px] tabular-nums text-muted-foreground">
							{t('Ref {{ reference }}', { reference: receipt_reference })}
						</span>
					</div>
				</div>
				<dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[11px]">
					<Detail
						label={t('Date')}
						value={format(parseISO(String(created)), 'dd.MM.yyyy HH:mm:ss')}
					/>
					<Detail label={t('Quantity')} value={quantity} />
					{isStaff && <Detail label={t('User')} value={account_name} />}
					<Detail
						label={t('Booking')}
						value={
							booking?.id
								? `${booking.booth?.booth_type?.name ?? ''}${booking.booth?.number ? ` #${booking.booth.number}` : ''}`.trim() ||
									`#${booking.reference}`
								: '—'
						}
					/>
					{storeCtx?.storeSettings?.vat_enabled && (
						<Detail
							label={t('VAT')}
							value={
								<>
									<Currency price={vat} /> ({vat_percentage}%)
								</>
							}
						/>
					)}
					<Detail label={t('Discount')} value={<Currency price={discount} />} />
					<Detail
						label={t('Commission')}
						value={
							<>
								<Currency price={commission} /> ({+commission_percentage}%)
							</>
						}
					/>
					<Detail
						label={t('Net profit')}
						emphasis
						value={<Currency price={{ amount: Number(net_profit), currency: total.currency }} />}
					/>
					<Detail label={t('Payout ID')} value={payout ? `#${payout}` : '—'} />
				</dl>
			</button>
		</li>
	)
}
